import { sanitizeClassName } from './htmlAttrs'

const UNSAFE_STYLE = /expression\s*\(|javascript\s*:|vbscript\s*:|moz-binding|@import/i
const SIZE_RE = /^\d+(\.\d+)?(px|%|em|rem|pt)?$/i
const COLOR_RE = /^(#[0-9a-f]{3,8}|[a-z]+)$/i

export type StyleMap = Array<[string, string]>

export function parseStyleDeclarations(style: string | null | undefined): StyleMap {
  if (!style) {
    return []
  }
  const declarations: string[] = []
  let current = ''
  let quote: "'" | '"' | null = null
  let depth = 0
  for (let i = 0; i < style.length; i++) {
    const char = style[i]
    if (quote) {
      current += char
      if (char === quote && style[i - 1] !== '\\') {
        quote = null
      }
      continue
    }
    if (char === '"' || char === "'") {
      quote = char
      current += char
      continue
    }
    if (char === '(') {
      depth += 1
      current += char
      continue
    }
    if (char === ')' && depth > 0) {
      depth -= 1
      current += char
      continue
    }
    if (char === ';' && depth === 0) {
      declarations.push(current)
      current = ''
      continue
    }
    current += char
  }
  if (current.trim()) {
    declarations.push(current)
  }

  const pairs: StyleMap = []
  for (const declaration of declarations) {
    const colon = declaration.indexOf(':')
    if (colon === -1) {
      continue
    }
    const property = declaration.slice(0, colon).trim().toLowerCase()
    const value = declaration.slice(colon + 1).trim()
    if (!property || !value || !/^-?[a-z0-9-]+$/.test(property)) {
      continue
    }
    if (UNSAFE_STYLE.test(property) || UNSAFE_STYLE.test(value)) {
      continue
    }
    pairs.push([property, value])
  }
  return pairs
}

export function serializeStyle(pairs: StyleMap): string | null {
  if (!pairs.length) {
    return null
  }
  return pairs.map(([property, value]) => `${property}: ${value}`).join('; ')
}

export function sanitizeStyle(value: string | null | undefined): string | null {
  return serializeStyle(parseStyleDeclarations(value))
}

export function styleProperty(style: string | null | undefined, property: string): string | null {
  const found = parseStyleDeclarations(style).find(([name]) => name === property)
  return found ? found[1] : null
}

export function setStyleProperty(style: string | null | undefined, property: string, value: string): string | null {
  const pairs = parseStyleDeclarations(style).filter(([name]) => name !== property)
  pairs.push([property, value])
  return serializeStyle(pairs)
}

export function removeStyleProperty(style: string | null | undefined, property: string): string | null {
  return serializeStyle(parseStyleDeclarations(style).filter(([name]) => name !== property))
}

export function hasAuthorTableWidth(attrs: { style?: string | null, width?: string | null }): boolean {
  if (attrs.width) {
    return true
  }
  return Boolean(
    styleProperty(attrs.style, 'width')
    || styleProperty(attrs.style, 'min-width')
    || styleProperty(attrs.style, 'max-width')
  )
}

export function sanitizeSize(value: string | null): string | null {
  if (!value) {
    return null
  }
  const trimmed = value.trim()
  return SIZE_RE.test(trimmed) ? trimmed : null
}

export function sanitizeBorderAttr(value: string | null): string | null {
  if (!value) {
    return null
  }
  const trimmed = value.trim()
  return /^\d{1,2}$/.test(trimmed) ? trimmed : null
}

export function sanitizeAlign(value: string | null): string | null {
  if (!value) {
    return null
  }
  const trimmed = value.trim().toLowerCase()
  return trimmed === 'left' || trimmed === 'center' || trimmed === 'right' || trimmed === 'justify'
    ? trimmed
    : null
}

export function sanitizeValign(value: string | null): string | null {
  if (!value) {
    return null
  }
  const trimmed = value.trim().toLowerCase()
  return trimmed === 'top' || trimmed === 'middle' || trimmed === 'bottom' || trimmed === 'baseline'
    ? trimmed
    : null
}

export function sanitizeColor(value: string | null): string | null {
  if (!value) {
    return null
  }
  const trimmed = value.trim()
  return COLOR_RE.test(trimmed) ? trimmed : null
}

export function sanitizeTableClass(value: string | null): string | null {
  return sanitizeClassName(value)
}

function columnCount(table: HTMLElement): number {
  const row = table.querySelector('tr')
  if (!row) {
    return 0
  }
  let count = 0
  for (const cell of Array.from(row.children)) {
    if (cell.tagName === 'TD' || cell.tagName === 'TH') {
      count += Math.max(1, parseInt(cell.getAttribute('colspan') || '1', 10) || 1)
    }
  }
  return count
}

// TipTap's default table layout writes min-width: 25px per column when the
// author did not set a width. Drop that exact declaration so reopening a
// document does not keep the injected size.
const INJECTED_COLUMN_MIN_PX = 25

export function styleFromTable(element: HTMLElement): string | null {
  const style = sanitizeStyle(element.getAttribute('style'))
  if (!style) {
    return null
  }
  const pairs = parseStyleDeclarations(style)
  if (pairs.length !== 1 || pairs[0][0] !== 'min-width') {
    return style
  }
  const match = /^(\d+)px$/.exec(pairs[0][1])
  const columns = columnCount(element)
  if (match && columns > 0 && Number(match[1]) === columns * INJECTED_COLUMN_MIN_PX) {
    return null
  }
  return style
}

function cellColumnIndex(cell: HTMLElement): number {
  let index = 0
  let previous = cell.previousElementSibling
  while (previous) {
    if (previous.tagName === 'TD' || previous.tagName === 'TH') {
      index += Math.max(1, parseInt(previous.getAttribute('colspan') || '1', 10) || 1)
    }
    previous = previous.previousElementSibling
  }
  return index
}

function columnElements(cell: HTMLElement): HTMLElement[] {
  const table = cell.closest('table')
  if (!table) {
    return []
  }
  return Array.from(table.querySelectorAll(':scope > colgroup > col'))
}

function widthFromElement(element: HTMLElement): string | null {
  const fromStyle = styleProperty(element.getAttribute('style'), 'width')
  if (fromStyle) {
    return fromStyle
  }
  const attr = element.getAttribute('width')?.trim()
  if (!attr || !SIZE_RE.test(attr)) {
    return null
  }
  return /^\d+(\.\d+)?$/.test(attr) ? `${attr}px` : attr
}

export function styleFromCell(element: HTMLElement): string | null {
  let style = sanitizeStyle(element.getAttribute('style'))
  const span = Math.max(1, parseInt(element.getAttribute('colspan') || '1', 10) || 1)
  if (span !== 1 || styleProperty(style, 'width') || element.getAttribute('width')) {
    return style
  }
  const cols = columnElements(element)
  const col = cols[cellColumnIndex(element)]
  const colWidth = col ? widthFromElement(col) : null
  if (!colWidth) {
    return style
  }
  return setStyleProperty(style, 'width', colWidth)
}

export function parseColwidth(element: HTMLElement): number[] | null {
  const attr = element.getAttribute('colwidth')
  if (attr) {
    const values = attr.split(',').map(part => parseInt(part.trim(), 10)).filter(value => value > 0)
    return values.length ? values : null
  }
  const span = Math.max(1, parseInt(element.getAttribute('colspan') || '1', 10) || 1)
  if (span < 2 || styleProperty(element.getAttribute('style'), 'width') || element.getAttribute('width')) {
    return null
  }
  const cols = columnElements(element)
  const start = cellColumnIndex(element)
  const widths: number[] = []
  for (let i = 0; i < span; i++) {
    const raw = cols[start + i] ? widthFromElement(cols[start + i]) : null
    const match = raw ? /^(\d+(?:\.\d+)?)px$/.exec(raw) : null
    if (!match) {
      return null
    }
    widths.push(Math.round(parseFloat(match[1])))
  }
  return widths
}

export function htmlAttribute(
  name: string,
  sanitize: (value: string | null) => string | null,
) {
  return {
    default: null as string | null,
    parseHTML: (element: HTMLElement) => sanitize(element.getAttribute(name)),
    renderHTML: (attributes: Record<string, unknown>) => {
      const value = attributes[name]
      if (value == null || value === '') {
        return {}
      }
      return { [name]: String(value) }
    },
  }
}
